import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Camera, ChevronRight, Copy, Eye, Laptop, LogOut, Smartphone, Trash2, X } from 'lucide-react';
import { ROLE_KEYS, ROLE_LABELS, type Member, type RoleKey } from '@unitly/contracts';
import {
  useChangePassword, useDeleteAvatar, useEmailNotificationPrefs, useInvitations, useInvite, useLogout, useMembers, useMyNickname,
  useRemoveMember, useRevokeInvitation, useRevokeSession, useSessions, useSetEmailNotificationPrefs, useSetNickname, usePushStatus, useSetPushSubscription, useTestPush, useUpdateMe,
  useUpdateMember, useUpdateOrg, useUploadAvatar,
} from '../api/queries.ts';
import { useOrgCtx } from '../app/org-context.tsx';
import { PageHeader } from '../features/shell/Chrome.tsx';
import { Avatar, PageLoader, Segmented, SelectField, TextField } from '../ui/primitives.tsx';
import { useToast } from '../ui/Toast.tsx';
import { applyTheme, readTheme, type ThemePref } from '../lib/theme.ts';
import { relativeTime } from '../lib/dates.ts';
import { currentSubscription, pushSupport, subscribeDevice } from '../lib/push.ts';
import { ApiError } from '../api/client.ts';
import { SETTINGS_LINKS } from './Menu.tsx';
import { IntegrationsSettings, SocialIntegrationsSettings } from '../features/settings/Integrations.tsx';
import { MemberAccessSheet } from '../features/settings/MemberAccessSheet.tsx';
import { DeveloperSettings } from '../features/settings/Developer.tsx';

export function SettingsPage() {
  const { section } = useParams();
  const { base, detail } = useOrgCtx();
  const current = SETTINGS_LINKS.find((s) => s.id === section);

  if (!current) {
    return (
      <div className="page wide">
        <PageHeader title="Impostazioni" back={`${base}/menu`} />
        <div className="card list settings-links">
          {SETTINGS_LINKS.filter((s) => !('admin' in s) || detail?.capabilities.admin).map((s) => (
            <Link key={s.id} to={`${base}/settings/${s.id}`} className="list-row">
              <span className="list-icon" style={{ background: s.color }}><s.icon size={17} /></span>
              <span className="grow">{s.label}</span>
              <ChevronRight className="chev" size={20} />
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page wide">
      <PageHeader title={current.label} back={`${base}/settings`} />
      {/* Desktop: two columns, so forms keep a readable width on a full-width page. */}
      {section === 'profile' && (
        <div className="settings-cols">
          <div><ProfileSettings /><NicknameSettings /></div>
          <div><DeviceNotificationSettings /><EmailNotificationSettings /></div>
        </div>
      )}
      {section === 'appearance' && <div className="settings-cols"><div><AppearanceSettings /></div></div>}
      {section === 'security' && <SecuritySettings />}
      {section === 'organization' && <div className="settings-cols"><div><OrganizationSettings /></div></div>}
      {section === 'members' && <MembersSettings />}
      {section === 'integrations' && <><IntegrationsSettings /><SocialIntegrationsSettings /></>}
      {section === 'developer' && (detail?.capabilities.admin ? <DeveloperSettings /> : <p className="hint">Solo gli amministratori gestiscono chiavi API e webhook.</p>)}
    </div>
  );
}

function ProfileSettings() {
  const { me } = useOrgCtx();
  const update = useUpdateMe();
  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();
  const toast = useToast();
  const [name, setName] = useState(me.user.displayName);
  const [tz, setTz] = useState(me.user.timeZone);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [me.user.timeZone];
  const err = update.error instanceof ApiError ? update.error : null;

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarError(null);
    uploadAvatar.mutate(file, {
      onSuccess: () => toast('Immagine del profilo aggiornata'),
      onError: (err) => setAvatarError(err instanceof ApiError ? err.message : 'Caricamento non riuscito.'),
    });
  };

  return (
    <>
      <form className="card card-pad form" onSubmit={(e) => { e.preventDefault(); update.mutate({ displayName: name, timeZone: tz }, { onSuccess: () => toast('Profilo aggiornato') }); }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div className="avatar-upload">
            <Avatar id={me.user.id} name={name || me.user.displayName} avatarUrl={me.user.avatarUrl} size={60} />
            <button type="button" className="avatar-upload-btn" aria-label="Cambia immagine del profilo"
              disabled={uploadAvatar.isPending} onClick={() => fileInputRef.current?.click()}>
              <Camera size={13} />
            </button>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={pickFile} />
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{me.user.email}</div>
            <div className="hint" style={{ padding: 0 }}>L’email identifica il tuo account.</div>
            {me.user.avatarUrl && (
              <button type="button" className="btn btn-plain btn-sm" style={{ padding: '2px 0', height: 'auto' }} disabled={deleteAvatar.isPending}
                onClick={() => deleteAvatar.mutate(undefined, { onSuccess: () => toast('Immagine del profilo rimossa') })}>
                <X size={13} /> Rimuovi immagine
              </button>
            )}
          </div>
        </div>
        {avatarError && <div className="form-error">{avatarError}</div>}
        <p className="hint" style={{ margin: 0 }}>JPG, PNG o WebP, max 5 MB.</p>
        <TextField label="Nome e cognome" value={name} onChange={(e) => setName(e.target.value)} error={err?.errors.displayName} required />
        <SelectField label="Fuso orario" value={tz} onChange={(e) => setTz(e.target.value)} error={err?.errors.timeZone}>
          {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
        </SelectField>
        <p className="hint">Il fuso orario decide cosa è “oggi” e “in ritardo” per te.</p>
        <button className="btn btn-primary" disabled={update.isPending || (name === me.user.displayName && tz === me.user.timeZone)}>Salva</button>
      </form>
    </>
  );
}

function NicknameSettings() {
  const { orgId } = useOrgCtx();
  const profile = useMyNickname(orgId);
  const setNickname = useSetNickname(orgId);
  const toast = useToast();
  const [value, setValue] = useState('');
  const [key, setKey] = useState(false);
  if (profile.data && !key) { setValue(profile.data.nickname ?? ''); setKey(true); }
  const err = setNickname.error instanceof ApiError ? setNickname.error : null;

  return (
    <form
      className="card card-pad form" style={{ marginTop: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        setNickname.mutate(value.trim() || null, { onSuccess: () => toast('Nickname aggiornato'), onError: (err) => toast(err instanceof ApiError ? err.message : 'Salvataggio non riuscito') });
      }}
    >
      <TextField
        label="Nickname" value={value} onChange={(e) => setValue(e.target.value.toLowerCase())} error={err?.errors.nickname}
        placeholder="es. m.rossi" maxLength={30}
      />
      <p className="hint">
        Usato in Chat per essere trovato dai tuoi colleghi (es. <strong>@{value.trim() || 'nickname'}</strong>). Deve essere unico nella tua organizzazione,
        ma la stessa combinazione può esistere in un’altra azienda su Unitly. Lettere minuscole, numeri, punti o underscore.
      </p>
      <button className="btn btn-primary" disabled={setNickname.isPending || !profile.data}>Salva</button>
    </form>
  );
}

const EMAIL_PREF_ROWS: { key: 'taskAssigned' | 'dailyDigest' | 'chat'; label: string; hint: string }[] = [
  { key: 'taskAssigned', label: 'Task assegnate', hint: 'Quando ti viene assegnata una nuova task.' },
  { key: 'dailyDigest', label: 'Riepilogo giornaliero', hint: 'Task in scadenza e in ritardo, eventi di oggi: un’unica email al mattino.' },
  { key: 'chat', label: 'Messaggi in chat', hint: 'Solo se un messaggio resta da leggere per qualche minuto.' },
];

function EmailNotificationSettings() {
  const { orgId } = useOrgCtx();
  const prefs = useEmailNotificationPrefs(orgId);
  const setPrefs = useSetEmailNotificationPrefs(orgId);

  if (!prefs.data) return null;
  const toggle = (key: 'taskAssigned' | 'dailyDigest' | 'chat') => setPrefs.mutate({ ...prefs.data!, [key]: !prefs.data![key] });

  return (
    <>
    <h3 className="subhead">Notifiche email</h3>
    <div className="card list">
      {EMAIL_PREF_ROWS.map((row) => (
        <div key={row.key} className="section-row">
          <span className="grow">
            {row.label}
            <small>{row.hint}</small>
          </span>
          <button
            type="button" role="switch" className="switch" aria-checked={prefs.data![row.key]} aria-label={row.label}
            disabled={setPrefs.isPending} onClick={() => toggle(row.key)}
          />
        </div>
      ))}
    </div>
    </>
  );
}

/** Pop-up notifications on this browser / installed web app (Web Push), turned on per device. */
function DeviceNotificationSettings() {
  const { orgId } = useOrgCtx();
  const toast = useToast();
  const support = pushSupport();
  /** undefined while reading the browser's subscription, null when there is none. */
  const [endpoint, setEndpoint] = useState<string | null | undefined>(support === 'supported' ? undefined : null);
  const [busy, setBusy] = useState(false);
  const status = usePushStatus(orgId, endpoint);
  const setSub = useSetPushSubscription(orgId);
  const testPush = useTestPush(orgId);
  useEffect(() => {
    if (support === 'supported') void currentSubscription().then((s) => setEndpoint(s?.endpoint ?? null), () => setEndpoint(null));
  }, [support]);

  const denied = support === 'supported' && Notification.permission === 'denied';
  const on = Boolean(status.data?.subscribed) && !denied;
  const available = support === 'supported' && Boolean(status.data?.configured) && !denied;

  const turnOn = async () => {
    setBusy(true);
    try {
      const sub = await subscribeDevice(status.data!.publicKey!);
      await setSub.mutateAsync({ on: true, subscription: sub.toJSON() });
      setEndpoint(sub.endpoint);
      toast('Notifiche attive su questo dispositivo');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message
        : err instanceof Error && err.message === 'denied' ? 'Hai bloccato le notifiche: riattivale dalle impostazioni del browser.'
        : err instanceof Error && err.message === 'dismissed' ? 'Notifiche non attivate.'
        : 'Non è stato possibile attivare le notifiche.';
      toast(msg);
    } finally {
      setBusy(false);
    }
  };
  const turnOff = () => endpoint && setSub.mutate({ on: false, endpoint }, { onSuccess: () => toast('Notifiche disattivate su questo dispositivo') });

  let hint: string;
  if (support === 'ios-install') hint = 'Su iPhone e iPad funzionano dall’app sulla schermata Home: in Safari tocca Condividi → “Aggiungi alla schermata Home”, apri Unitly da lì e attivale qui.';
  else if (support === 'unsupported') hint = 'Questo browser non supporta le notifiche.';
  else if (status.data && !status.data.configured) hint = 'Non ancora disponibili: manca la configurazione sul server.';
  else if (denied) hint = 'Sono bloccate per Unitly: riattivale dalle impostazioni del browser (su iPhone: Impostazioni → Notifiche → Unitly).';
  else if (on) hint = 'Attive: assegnazioni, commenti, messaggi e richieste arrivano qui anche con Unitly chiusa.';
  else hint = 'Ricevi un avviso su questo dispositivo per assegnazioni, commenti, messaggi e richieste.';

  return (
    <>
      <h3 className="subhead">Notifiche sul dispositivo</h3>
      <div className="card list">
        <div className="section-row">
          <span className="grow">
            Notifiche popup su questo dispositivo
            <small>{hint}</small>
          </span>
          <button
            type="button" role="switch" className="switch" aria-checked={on} aria-label="Notifiche popup su questo dispositivo"
            disabled={!available || busy || setSub.isPending} onClick={() => (on ? turnOff() : void turnOn())}
          />
        </div>
        {on && endpoint && (
          <button
            type="button" className="list-row list-more" disabled={testPush.isPending}
            onClick={() => testPush.mutate(endpoint, { onSuccess: (r) => toast(r.delivered ? 'Notifica di prova inviata' : 'Il dispositivo non ha risposto: disattiva e riattiva'), onError: (e) => toast(e.message) })}
          >Invia una notifica di prova</button>
        )}
      </div>
    </>
  );
}

function AppearanceSettings() {
  const [theme, setTheme] = useState<ThemePref>(readTheme);
  useEffect(() => applyTheme(theme), [theme]);
  return (
    <div className="card card-pad form">
      <span className="hint" style={{ padding: 0 }}>Tema</span>
      <Segmented label="Tema" value={theme} onChange={setTheme} options={[{ value: 'system', label: 'Sistema' }, { value: 'light', label: 'Chiaro' }, { value: 'dark', label: 'Scuro' }]} />
      <p className="hint" style={{ padding: 0 }}>“Sistema” segue le impostazioni del tuo dispositivo.</p>
    </div>
  );
}

function ChangePasswordForm() {
  const change = useChangePassword();
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const err = change.error instanceof ApiError ? change.error : null;

  return (
    <form
      className="card card-pad form"
      onSubmit={(e) => {
        e.preventDefault();
        change.mutate({ currentPassword, newPassword }, {
          onSuccess: () => { toast('Password aggiornata'); setCurrentPassword(''); setNewPassword(''); },
        });
      }}
    >
      <strong style={{ fontSize: 17 }}>Cambia password</strong>
      <TextField label="Password attuale" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} error={err?.errors.currentPassword} required />
      <TextField label="Nuova password" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} error={err?.errors.newPassword} hint="Almeno 12 caratteri." required minLength={12} />
      {err && !err.errors.currentPassword && !err.errors.newPassword && <div className="form-error">{err.message}</div>}
      <button className="btn btn-primary" disabled={change.isPending || !currentPassword || !newPassword}>Salva nuova password</button>
      <p className="hint" style={{ margin: 0 }}>Gli altri dispositivi verranno disconnessi.</p>
    </form>
  );
}

function SecuritySettings() {
  const sessions = useSessions();
  const revoke = useRevokeSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const toast = useToast();
  return (
    <div className="settings-cols">
      <div><ChangePasswordForm /></div>
      <div>
      <h3 className="subhead">Dispositivi con accesso attivo</h3>
      {sessions.isLoading && <PageLoader />}
      <div className="card list">
        {sessions.data?.map((s) => (
          <div className="list-row" key={s.id}>
            <span className="list-icon" style={{ background: 'var(--fill)', color: 'var(--label-2)' }}>
              {/iPhone|Android/.test(s.deviceLabel) ? <Smartphone size={17} /> : <Laptop size={17} />}
            </span>
            <span className="grow">
              <div>{s.deviceLabel}{s.current && <span className="pill" style={{ marginLeft: 8, height: 22 }} data-cat="done">Questo dispositivo</span>}</div>
              <div className="secondary">Attivo {relativeTime(s.lastSeenAt)}{s.ip ? ` · ${s.ip}` : ''}</div>
            </span>
            {s.current ? (
              <button type="button" className="btn btn-plain btn-sm" onClick={() => logout.mutate(undefined, { onSuccess: () => navigate('/login') })}><LogOut size={15} /> Esci</button>
            ) : (
              <button type="button" className="btn btn-danger btn-sm" onClick={() => revoke.mutate(s.id, { onSuccess: () => toast('Dispositivo disconnesso') })}>Disconnetti</button>
            )}
          </div>
        ))}
      </div>
      <p className="hint" style={{ marginTop: 10 }}>Se non riconosci un dispositivo, disconnettilo e cambia la password.</p>
      </div>
    </div>
  );
}

function OrganizationSettings() {
  const { orgId, org } = useOrgCtx();
  const update = useUpdateOrg(orgId);
  const toast = useToast();
  const [name, setName] = useState(org.name);
  return (
    <form className="card card-pad form" onSubmit={(e) => { e.preventDefault(); update.mutate({ name }, { onSuccess: () => toast('Organizzazione aggiornata') }); }}>
      <TextField label="Nome dell’organizzazione" value={name} onChange={(e) => setName(e.target.value)} required />
      <TextField label="Indirizzo" value={`/o/${org.slug}`} readOnly hint="L’indirizzo non cambia quando rinomini l’organizzazione." />
      <button className="btn btn-primary" disabled={update.isPending || name.trim() === org.name}>Salva</button>
    </form>
  );
}

function MembersSettings() {
  const { orgId, detail, me } = useOrgCtx();
  const isAdmin = Boolean(detail?.capabilities.admin);
  const canInvite = Boolean(detail?.capabilities.invite);
  const members = useMembers(orgId);
  const invitations = useInvitations(orgId, canInvite);
  const invite = useInvite(orgId);
  const revokeInvite = useRevokeInvitation(orgId);
  const updateMember = useUpdateMember(orgId);
  const removeMember = useRemoveMember(orgId);
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Exclude<RoleKey, 'owner'>>('member');
  const [link, setLink] = useState<string | null>(null);
  const [accessFor, setAccessFor] = useState<Member | null>(null);
  const err = invite.error instanceof ApiError ? invite.error : null;

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast('Link copiato'); } catch { toast('Copia non riuscita: seleziona il link a mano'); }
  };

  return (
    <div className={canInvite ? 'settings-cols' : undefined}>
      {canInvite && (
      <div>
        <form className="card card-pad form" style={{ marginBottom: 24 }} onSubmit={(e) => {
          e.preventDefault();
          invite.mutate({ email, role }, { onSuccess: (inv) => { setLink(inv.inviteUrl ?? null); setEmail(''); } });
        }}>
          <strong style={{ fontSize: 17 }}>Invita una persona</strong>
          <TextField label="Email" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} error={err?.errors.email} required />
          <SelectField label="Ruolo" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
            {ROLE_KEYS.filter((r) => r !== 'owner' && (r !== 'admin' || detail?.role === 'owner')).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </SelectField>
          {err && !err.errors.email && <div className="form-error">{err.message}</div>}
          <button className="btn btn-primary" disabled={invite.isPending}>Crea invito</button>
          {link && (
            <div className="form" style={{ gap: 8 }}>
              <div className="invite-link">
                <code>{link}</code>
                <button type="button" className="icon-btn" aria-label="Copia link" onClick={() => copy(link)}><Copy size={17} /></button>
              </div>
              <p className="hint">Se l’invio email è configurato sul server, questo link è arrivato anche via email all’indirizzo invitato. Puoi comunque condividerlo tu stesso (valido 7 giorni, utilizzabile una volta).</p>
            </div>
          )}
        </form>

      {invitations.data && invitations.data.length > 0 && (
        <>
          <h3 className="subhead">Inviti in attesa</h3>
          <div className="card list" style={{ marginBottom: 24 }}>
            {invitations.data.map((i) => (
              <div className="list-row" key={i.id}>
                <span className="grow">{i.email}<div className="secondary">{ROLE_LABELS[i.role]} · scade {new Date(i.expiresAt).toLocaleDateString('it-IT')}</div></span>
                <button type="button" className="icon-btn muted" aria-label={`Revoca invito a ${i.email}`} onClick={() => revokeInvite.mutate(i.id)}><Trash2 size={17} /></button>
              </div>
            ))}
          </div>
        </>
      )}
      </div>
      )}

      <div>
      <h3 className="subhead">Membri {members.data && `· ${members.data.length}`}</h3>
      {members.isLoading && <PageLoader />}
      {members.error && <div className="form-error">{members.error.message}</div>}
      <div className="card list">
        {members.data?.map((m) => (
          <div className="list-row" key={m.userId}>
            <Avatar id={m.userId} name={m.displayName} avatarUrl={m.avatarUrl} size={36} />
            <span className="grow">
              <div>{m.displayName}{m.userId === me.user.id && <span className="secondary"> (tu)</span>}</div>
              <div className="secondary">{m.email}</div>
            </span>
            {isAdmin && m.role !== 'owner' && m.role !== 'admin' && (
              <button type="button" className="icon-btn muted" aria-label={`Cosa può vedere ${m.displayName}`} onClick={() => setAccessFor(m)}><Eye size={17} /></button>
            )}
            {isAdmin && m.role !== 'owner' && m.userId !== me.user.id ? (
              <>
                <select
                  className="select"
                  style={{ width: 'auto', minHeight: 32, padding: '4px 30px 4px 10px', fontSize: 14 }}
                  value={m.role}
                  aria-label={`Ruolo di ${m.displayName}`}
                  onChange={(e) => updateMember.mutate({ userId: m.userId, role: e.target.value as RoleKey }, {
                    onSuccess: () => toast('Ruolo aggiornato'),
                    onError: (er) => toast(er.message),
                  })}
                >
                  {ROLE_KEYS.filter((r) => r !== 'owner' && (r !== 'admin' || detail?.role === 'owner' || m.role === 'admin')).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
                <button type="button" className="icon-btn muted" aria-label={`Rimuovi ${m.displayName}`} onClick={() => {
                  if (confirm(`Rimuovere ${m.displayName} dall’organizzazione?`)) removeMember.mutate(m.userId, { onSuccess: () => toast(`${m.displayName} rimosso`) });
                }}><Trash2 size={17} /></button>
              </>
            ) : (
              <span className="pill">{ROLE_LABELS[m.role]}</span>
            )}
          </div>
        ))}
      </div>
      </div>
      <MemberAccessSheet open={Boolean(accessFor)} onClose={() => setAccessFor(null)} member={accessFor} />
    </div>
  );
}
